/* Audit Assistant — panel header. Avatar, name, subtitle, online dot,
   New Chat, Clear, Settings (disabled), Close.

   The actions are icon-only buttons; `data-tip` drives the CSS tooltip in
   audit-assistant.css (no `title`, which would double up with a slow,
   unstyled native popup). aria-label carries the name for screen readers. */
import type { JSX } from 'react';

interface ChatHeaderProps {
  onNewChat: () => void;
  onClear: () => void;
  onClose: () => void;
}

/** Pencil on a square — start a fresh conversation. */
function NewChatIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" />
      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
    </svg>
  );
}

/** Broom-free eraser: a trash can — wipe the current conversation. */
function ClearIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function SettingsIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.2.61.77 1.02 1.42 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  );
}

function CloseIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export function ChatHeader({ onNewChat, onClear, onClose }: ChatHeaderProps): JSX.Element {
  return (
    <header className="aa-header">
      <div className="aa-header-id">
        <div className="aa-avatar aa-avatar-lg" aria-hidden="true">
          AI
          <span className="aa-online" data-tip="Online" />
        </div>
        <div className="aa-header-text">
          <div className="aa-title">Audit Assistant</div>
          <div className="aa-subtitle">AI-powered Investment Audit Assistant</div>
        </div>
      </div>
      <div className="aa-header-actions">
        <button
          type="button"
          className="aa-hbtn aa-hbtn-icon aa-hbtn-primary"
          onClick={onNewChat}
          aria-label="New chat"
          data-tip="New chat"
        >
          <NewChatIcon />
        </button>
        <button
          type="button"
          className="aa-hbtn aa-hbtn-icon"
          onClick={onClear}
          aria-label="Clear conversation"
          data-tip="Clear conversation"
        >
          <ClearIcon />
        </button>
        <button
          type="button"
          className="aa-hbtn aa-hbtn-icon"
          disabled
          aria-disabled="true"
          aria-label="Settings (coming soon)"
          data-tip="Settings — coming soon"
        >
          <SettingsIcon />
        </button>
        <button
          type="button"
          className="aa-hbtn aa-hbtn-icon"
          onClick={onClose}
          aria-label="Close Audit Assistant"
          data-tip="Close"
        >
          <CloseIcon />
        </button>
      </div>
    </header>
  );
}
